import type { VenueDto } from '@_/api-contracts/public-api.ts';
import clsx from 'clsx';
import { FormattedMessage } from 'react-intl';
import { quantityCommonI18n } from '@_/i18n-common';
import {
  DotSymbol,
  FormattedCountry,
  FormattedNum,
  Image,
  Link,
  PlaceholderImage,
  Rating,
  Text
} from '@_/ui';
import { LinkOptionsBuilder } from '#src/lib/LinkOptionsBuilder.ts';

export function VenueList({
  items,
  numColumns = 4
}: {
  items: VenueDto[];
  numColumns?: 2 | 3 | 4;
}) {
  return (
    <div
      className={clsx(
        'flex flex-col gap-6 sm:grid sm:gap-8',
        numColumns === 2 && 'sm:grid-cols-2',
        numColumns === 3 && 'sm:grid-cols-2 md:grid-cols-3',
        numColumns === 4 && 'sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
      )}
    >
      {items.map(item => (
        <VenueListItem key={item.id} item={item} />
      ))}
    </div>
  );
}

interface VenueListItemProps {
  item: VenueDto;
}

export function VenueListItem({ item }: VenueListItemProps) {
  const ratingDisplay = item.rating ? <Rating condensed value={item.rating} /> : undefined;

  const followersDisplay = item.numFollowers ? (
    <div className="flex items-center gap-x-1">
      <Text subdued>
        <FormattedMessage
          {...quantityCommonI18n.numFollowersCompact}
          values={{
            count: item.numFollowers,
            num: <FormattedNum compact value={item.numFollowers} />
          }}
        />
      </Text>
    </div>
  ) : undefined;

  return (
    <Link
      {...LinkOptionsBuilder.venue({ venue: item })}
      className="flex gap-x-3 sm:block sm:gap-0"
      plain
    >
      <div className="aspect-[4/3] w-24 flex-none overflow-hidden rounded-lg bg-gray-100 sm:w-full">
        {item.images?.length ? (
          <Image className="h-full w-full" fit="cover" src={item.images[0].imageUrl} />
        ) : (
          <PlaceholderImage className="aspect-[4/3]" variant={2} />
        )}
      </div>

      <div className="relative flex w-full min-w-0 flex-col gap-y-1 sm:mt-3">
        <div className="flex justify-between gap-x-4">
          <Text block bold>
            {item.name}
          </Text>
          <div className="hidden sm:block">{ratingDisplay}</div>
        </div>

        <div className="flex items-center gap-x-1">
          <Text block subdued>
            {item.addressZones.locality || item.addressZones.zone2 || item.addressZones.zone1 || (
              <FormattedCountry value={item.countryCode!} />
            )}
          </Text>
          {!!followersDisplay && (
            <>
              <DotSymbol className="hidden sm:block" />
              <div className="hidden sm:block">{followersDisplay}</div>
            </>
          )}
        </div>

        <div className="flex items-center gap-x-1 sm:hidden">
          {ratingDisplay}
          {!!ratingDisplay && !!followersDisplay && <DotSymbol className="sm:hidden" />}
          {followersDisplay}
        </div>
      </div>
    </Link>
  );
}
